// THE SELECTION BRACKET — four right triangles, converging on their subject.
//
// It replaces the diamond ring that framed a star, a body and a neighbouring galaxy in three different places,
// built by two different functions that had drifted into two different line weights. One shape, defined once,
// used by the galaxy view, the universe field and the system view — so a marker around a galaxy and one around a
// moon read as the same object at two sizes, which is the only reason a shared marker is worth having.
//
// THE SHAPE IS THE EXPLORE ICON'S, and not by coincidence: that glyph is a world with four converging markers
// around it, and this is the same gesture at world scale. It was traced from the square-lattice glyph the family
// used before it moved to the logo's triangle lattice (Oct 4, tools/build-hex-icons.mjs) -- a 16x16 box, y DOWN:
//
//     M4 0 0 4 4 4Z   M12 0 16 4 12 4Z   M4 16 0 12 4 12Z   M12 16 16 12 12 12Z
//
// which is, per corner, a right angle at 4 units in from that corner on both axes, with the legs running out to
// the box edge. Centred and normalised to a half-extent of 1, that right angle sits at 0.5 — GAP below. The
// numbers here are that path, converted, rather than a fresh set that happens to look similar.
import * as THREE from '/lib/three/three.module.min.js';

// TWO NUMBERS, NOT ONE. The first cut scaled a single unit shape, which welded the TICK SIZE to the STANDOFF:
// with the glyph's proportions a triangle's legs are half the bracket's half-extent, so the ticks grew with
// whatever they framed. Measured on the shipped build: a 15 px star wore a 105 px bracket with 26 px ticks, and a
// large nearby planet wore ticks that filled the frame.
//
// A focus bracket does not work like that. Its weight is constant — the marks are the same size wherever you
// point it — and only its SPREAD answers the subject. So a bracket now carries a standoff, in world units,
// and a tick length, in PIXELS, and the geometry is rewritten from the two. The glyph's gesture is unchanged:
// four filled right triangles, right angle inward, legs running out. Only the ratio between them is free.
export const TICK_PX = 12;          // the length of one leg, on screen, everywhere
// …AND HOW FAR APART THEY MAY SPREAD, as a fraction of the SHORTER viewport edge rather than a flat number of
// pixels. A fixed ceiling is a different thing on a phone than on a desktop: 180 px is a comfortable frame in a
// 1400 px window and wider than the whole viewport on a 294 px one, where it would put the corners off-screen —
// which is the same complaint as the original bug, arriving by a different route.
export const MAX_SPREAD = 0.34;

const CORNERS = [[-1, 1], [1, 1], [-1, -1], [1, -1]];

// A bracket is ONE mesh of four triangles, as before — the vertices are rewritten rather than the mesh rebuilt,
// which is 36 floats a frame and cheaper than the four-object group the same shape would otherwise need.
export function bracketGeo() {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(36), 3));
  g.userData.shape = null;
  return g;
}

// HOW FAR THE BRACKET REACHES FROM ITS SUBJECT, in screen pixels — the standoff plus one tick, which is the
// outer edge of the frame. `clearance` is world units: how far outside the subject the right angle should sit.
// The standoff is clamped there: never closer than the ticks are long (they would meet in the middle and read as
// a box), never further than the frame allows (a planet filling the screen would otherwise push them off it).
//
// PURE, AND SEPARATE FROM DRAWING, because something else has to stay out of the bracket's way: the figure
// standing on the subject floats above the selector rather than inside it, and it is positioned on a different
// pass from the one that draws the frame. Asking the same function means the gap cannot drift.
export function bracketReachPx(clearance, camDist, view, opts = {}) {
  const tickPx = opts.tickPx ?? TICK_PX;
  const upp = camDist / view.pxPerRad;                  // world units per screen pixel, at that distance
  const standoffPx = Math.min(Math.max(clearance / upp, tickPx * 0.9), view.maxStandoffPx);
  return { standoffPx, tickPx, reachPx: standoffPx + tickPx, upp };
}

// HOW FAR ABOVE THAT REACH THE FIGURE STANDS. Small: the point is that the suit clears the frame, not that it
// hovers somewhere unrelated to the thing it is standing on.
export const FIGURE_CLEAR_PX = 8;

// …AND THE VERTICES THEMSELVES, written straight into the buffer from that reach.
export function setBracket(mesh, clearance, camDist, view, opts = {}) {
  const { standoffPx, tickPx, upp } = bracketReachPx(clearance, camDist, view, opts);
  const standoff = standoffPx * upp, tick = tickPx * upp;
  const pos = mesh.geometry.attributes.position, a = pos.array;
  let i = 0;
  for (const [sx, sy] of CORNERS) {
    const cx = sx * standoff, cy = sy * standoff;
    a[i++] = cx;             a[i++] = cy + sy * tick; a[i++] = 0;   // out along the vertical leg
    a[i++] = cx + sx * tick; a[i++] = cy;             a[i++] = 0;   // out along the horizontal leg
    a[i++] = cx;             a[i++] = cy;             a[i++] = 0;   // the right angle, pointing at the subject
  }
  pos.needsUpdate = true;
  mesh.geometry.userData.shape = { standoff, tick };
  return mesh;
}

// THE THREE READINGS, and there are three rather than four. The diamonds had one more — a grey framing for the
// focused object in an orbit view — which said something the white one already said more clearly. What is left
// is: you, something you are pointing at, and something you are only looking at.
export const BRACKET = {
  here:  { color: 0xffffff, opacity: 1.00 },   // where you are, or what you have selected
  hover: { color: 0xffffff, opacity: 0.45 },   // under the cursor, not chosen
  // The mode's colour, and it is NOT a local choice: the stylesheet, the viewport edge, the hotbar and the scout
  // chip all say #8BC5E7, and the suite asserts the copies agree rather than trusting modules with no shared
  // scope to stay in step. Changing it here changes a marker; changing the mode's colour means changing them all.
  scout: { color: 0x8BC5E7, opacity: 0.90 },
};

// THE BREATH ON A "YOU ARE HERE" MARK, and one formula for every view that draws one. It ran the whole way down
// to a quarter opacity once, so for part of every second the mark on the place you were was fainter than the
// faint mark on the thing beside it, and the eye read the wrong one as chosen. The top third of the range is
// still alive and never dimmer than the marks it has to outrank. Phase comes from the wall clock rather than a
// per-view timer, so the galaxy's mark and the system's are in step through the dive that shows both at once.
export const HERE_PULSE_LOW = 0.65;
export const herePulse = (nowMs) =>
  HERE_PULSE_LOW + ((Math.cos(nowMs / 1000 * Math.PI * 2) + 1) / 2) * (1 - HERE_PULSE_LOW);

// THE CLOCK EVERY MARK KEEPS TIME BY — the breath above and the avatar's drift — which is the wall clock except
// across a galaxy crossing. A crossing rebuilds the whole view behind a still of its last frame, and the marks in
// that still are frozen mid-breath and mid-drift while the live ones underneath carried on. The still then
// dissolves off the top, and two copies of one avatar a few pixels apart, one bracket at two brightnesses, blend
// into each other on the way. So the marks' clock stops when the still is taken and starts again once it has
// gone: the new view's marks resume exactly where the still froze them, and the dissolve crosses identical marks.
//
// One module-level clock rather than a flag per view, because both views import this one module and a crossing
// replaces the view that would own a flag. Paused only briefly by design: a pause that is never released cannot
// be allowed to freeze every mark in the game, so it releases itself after MARK_PAUSE_MAX_MS whatever happens.
export const MARK_PAUSE_MAX_MS = 4000;
const _markClock = { off: 0, pausedAt: null };
export function markNow(wallMs = Date.now()) {
  const w = Number(wallMs);
  if (_markClock.pausedAt !== null) {
    if (w - _markClock.pausedAt <= MARK_PAUSE_MAX_MS) return _markClock.pausedAt - _markClock.off;
    resumeMarks(w);
  }
  return w - _markClock.off;
}
export function pauseMarks(wallMs = Date.now()) {
  if (_markClock.pausedAt === null) _markClock.pausedAt = Number(wallMs);
}
export function resumeMarks(wallMs = Date.now()) {
  if (_markClock.pausedAt === null) return;
  // Resumed at the moment the pause would have expired if it overran, so an expired pause costs exactly
  // MARK_PAUSE_MAX_MS of shift and not the whole of however long it was left.
  const at = Math.min(Number(wallMs), _markClock.pausedAt + MARK_PAUSE_MAX_MS);
  _markClock.off += Math.max(0, at - _markClock.pausedAt);
  _markClock.pausedAt = null;
}
export const marksPaused = () => _markClock.pausedAt !== null;

// `depthTest: false` is the system view's convention for its markers and the right one everywhere: a bracket is
// chrome about a subject, not a thing in the scene that can be behind it. DoubleSide because it is billboarded
// and a camera that crosses its plane must not blink it out — and because a mirrored corner winds the other way.
export function makeBracket(kind, extra = {}) {
  const spec = BRACKET[kind] || BRACKET.here;
  const m = new THREE.Mesh(bracketGeo(), new THREE.MeshBasicMaterial({
    color: spec.color, opacity: spec.opacity, transparent: true,
    side: THREE.DoubleSide, depthTest: false, depthWrite: false, ...extra,
  }));
  m.userData.baseOpacity = spec.opacity;   // what a fade multiplies, so a crossfade has something to return to
  m.visible = false;
  return m;
}

// WHAT A BRACKET NEEDS TO KNOW ABOUT THE VIEW: how many pixels a radian is worth, which converts a tick length
// in pixels into world units, and how wide the frame is, which is what bounds the spread. Read from the live
// canvas on every call rather than cached — the fov moves during a dive, and the canvas moves on a resize.
export function bracketView(camera, el) {
  const w = el.clientWidth || window.innerWidth, h = el.clientHeight || window.innerHeight;
  return { pxPerRad: h / (2 * Math.tan(camera.fov * Math.PI / 360)),
           maxStandoffPx: Math.min(w, h) * MAX_SPREAD };
}
